import { useRef } from 'react';

import { type Query, useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type {
	FacebookGroupRecommendation,
	FacebookPostDataResult,
	ShareRecommendationsStatus,
} from '#src/app/pages/psf/shared/api';
import { getFacebookPostData } from '#src/app/pages/psf/shared/api';

export type UseFacebookPostDataResult =
	| { readonly status: 'pending' }
	| {
			readonly status: 'ready';
			readonly caption: string;
			readonly groups: readonly FacebookGroupRecommendation[];
			readonly recommendationsStatus: ShareRecommendationsStatus;
	  }
	| {
			readonly status: 'not-ready';
			readonly reason: 'no-caption' | 'no-groups' | 'fetch-error';
			readonly recommendationsStatus: ShareRecommendationsStatus | null;
	  };

const MAX_RETRIES = 1;

const GENERATING_POLL_INTERVAL_MS = 3000;
const MAX_GENERATING_POLLS = 10;

/**
 * The aggregate status is GENERATING while either Facebook or Reddit is still working, so an empty
 * Facebook list is not final yet. The middle-end coerces GENERATING older than 60s to ERROR.
 */
function getRefetchInterval({ state }: Query<FacebookPostDataResult>): number | false {
	if (state.status === 'error' || !state.data) return false;
	const { data } = state;
	if (data.kind !== 'not-ready' || data.reason !== 'no-groups' || data.recommendationsStatus !== 'GENERATING') {
		return false;
	}
	return state.dataUpdateCount <= MAX_GENERATING_POLLS ? GENERATING_POLL_INTERVAL_MS : false;
}

/** `no-caption` is not polled: `RESOURCE_NOT_FOUND` on a PSF page almost always means no caption was ever generated. */
export function useFacebookPostData(slugOrId: string, enabled: boolean): UseFacebookPostDataResult {
	const utilityContext = useUtilityContext();

	const consecutiveErrorsRef = useRef<Record<string, number>>({});

	// eslint-disable-next-line @tanstack/query/exhaustive-deps -- consecutiveErrorsRef is a stable useRef; utilityContext is stable; .current mutations are side effects, not query identity dependencies
	const query = useQuery<FacebookPostDataResult>({
		queryKey: ['psf-facebook-post-data', slugOrId],
		queryFn: async () => {
			const consecutiveErrors = consecutiveErrorsRef.current[slugOrId] ?? 0;
			try {
				const result = await getFacebookPostData(
					utilityContext,
					{ slugOrId },
					{ isFinalRetryAttempt: consecutiveErrors >= MAX_RETRIES },
				);
				consecutiveErrorsRef.current[slugOrId] = 0;
				return result;
			} catch (error) {
				// Reset after the final attempt, so a later fetch gets its own retry before it reports.
				consecutiveErrorsRef.current[slugOrId] = consecutiveErrors >= MAX_RETRIES ? 0 : consecutiveErrors + 1;
				throw error;
			}
		},
		enabled,
		retry: MAX_RETRIES,
		refetchInterval: getRefetchInterval,
		refetchIntervalInBackground: false,
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	// Checked before `data`: a failed poll keeps the stale GENERATING result, which must not be reported as current.
	if (query.isError) {
		return { status: 'not-ready', reason: 'fetch-error', recommendationsStatus: null };
	}

	if (query.data) {
		return query.data.kind === 'ready'
			? {
					status: 'ready',
					caption: query.data.caption,
					groups: query.data.groups,
					recommendationsStatus: query.data.recommendationsStatus,
				}
			: { status: 'not-ready', reason: query.data.reason, recommendationsStatus: query.data.recommendationsStatus };
	}

	return { status: 'pending' };
}
